import { useMutation } from '@apollo/client/react';
import { Button, Form, Input, Modal } from 'antd';
import { fill, values } from 'lodash';
import React, { useContext } from 'react';
import { AppContext } from '../../AppContext';
import { AGENCY_TYPE_KEYS, GA_EVENT, GA_LABEL } from '../../common/constants';
import { Event } from '../../common/trackEvents';
import { formValidatorRules, titleCase } from '../../common/utils';
import CommonSelect from '../../components/CommonSelect';
import { CREATE_AGENCY, UPDATE_AGENCY } from './graphql/Mutation';

const { required, characterWithoutWhiteSpace } = formValidatorRules;

const AddAgencyModal = (props) => {
  const {
    isUpdate,
    showModal,
    setShowModal,
    agencyData,
    setAgencyData,
    refetchAgencyDataWithInitialValues,
    setAgencyList,
    agencyList,
    onlyGetAgencyData = false,
    agencyId,
  } = props;
  const [form] = Form.useForm();
  const { getCurrentUser } = useContext(AppContext);
  const currentUser = getCurrentUser();

  const handleCancel = () => {
    setShowModal(false);
    setAgencyData();
    form.resetFields();
  };

  const [createAgency, { loading: createLoading }] = useMutation(
    CREATE_AGENCY,
    {
      onError() {},
      onCompleted() {
        Event(GA_EVENT.ADD_GLOBAL_AGENCY, {
          label: GA_LABEL.ADD_GLOBAL_AGENCY,
          // eslint-disable-next-line no-undef
          pathname: window?.location?.href,
          user_id: currentUser?.id,
          user_name: currentUser?.name,
          tenant_id: currentUser?.tenantUser?.tenant?.id,
          tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
        });
        setAgencyData();
        form.resetFields();
        setShowModal(false);
        if (refetchAgencyDataWithInitialValues) {
          refetchAgencyDataWithInitialValues();
        }
      },
    },
  );

  const [updateAgency, { loading: updateLoading }] = useMutation(
    UPDATE_AGENCY,
    {
      onError() {},
      onCompleted() {
        Event(GA_EVENT.EDIT_GLOBAL_AGENCY, {
          label: GA_LABEL.EDIT_GLOBAL_AGENCY,
          // eslint-disable-next-line no-undef
          pathname: window?.location?.href,
          agency_id: agencyData?.id,
          user_id: currentUser?.id,
          user_name: currentUser?.name,
          tenant_id: currentUser?.tenantUser?.tenant?.id,
          tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
        });
        setAgencyData();
        form.resetFields();
        setShowModal(false);
        if (refetchAgencyDataWithInitialValues) {
          refetchAgencyDataWithInitialValues();
        }
      },
    },
  );

  const onAgencySubmitFinish = async (formValues) => {
    const newFormValues = {
      ...formValues,
    };

    if (onlyGetAgencyData) {
      if (isUpdate) {
        fill(agencyList, newFormValues, agencyId, Number(agencyId) + 1);
      } else {
        setAgencyList([...agencyList, newFormValues]);
      }
      setAgencyData();
      setShowModal(false);
    } else {
      const variables = isUpdate
        ? { data: newFormValues, id: agencyData.id }
        : newFormValues;

      try {
        if (isUpdate) {
          await updateAgency({
            variables: { ...variables },
          });
          return;
        }
        await createAgency({
          variables: { data: { ...variables } },
        });
      } catch (error) {
        return error;
      }
    }
  };

  return (
    <Modal
      id="add-agency-modal"
      maskClosable={false}
      centered
      open={showModal}
      className="dialog"
      footer={null}
      closable={false}
    >
      <h2 id="add-agency-modal-title">{isUpdate ? 'Edit Team' : 'Add Team'}</h2>
      <Form
        id="add-agency-form"
        form={form}
        initialValues={agencyData || { isActive: true }}
        layout="vertical"
        onFinish={onAgencySubmitFinish}
      >
        <Form.Item
          rules={[
            required,
            characterWithoutWhiteSpace,
            {
              max: 250,
              message: 'Name cannot be more than 250 characters',
            },
          ]}
          name="name"
          label="Name"
        >
          <Input id="agency-name-input" allowClear placeholder="Enter Name" />
        </Form.Item>
        <Form.Item
          rules={[{ required, message: 'Required' }]}
          name="type"
          label="Type"
        >
          <CommonSelect
            id="agency-type-select"
            options={[
              ...values(AGENCY_TYPE_KEYS).map((type) => {
                return {
                  key: type,
                  value: type,
                  label: titleCase(type),
                };
              }),
            ]}
            className="mr-3"
            placeholder="Select Type"
            allowClear
          />
        </Form.Item>
        <div className="form-buttons">
          <Button
            id="add-agency-cancel-btn"
            shape="round"
            className="cancel-button"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            id="add-agency-save-btn"
            shape="round"
            type="primary"
            className="save-button"
            htmlType="submit"
            loading={createLoading || updateLoading}
          >
            {isUpdate ? 'Save' : 'Add'}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default AddAgencyModal;
